import { Reducer } from 'redux'
import { all, call, put, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import {
  CategorizedIntegrations,
  ProFacingWalletProduct,
  WalletProductOrg,
} from 'resources/externalKeys/walletProductIntegrations/types'
import restClient from 'restClient'
import { RootState } from 'types/state'

/** Raw API shape: activation is flattened as activation_id + activation_config. */
type RawProFacingWalletProduct = Omit<ProFacingWalletProduct, 'activation' | 'is_active' | 'activation_status'> & {
  activation_id?: number | null
  activation_config?: Record<string, unknown>
  activation_status?: string | null
  is_active?: boolean
}

export function normalizeIntegration(raw: RawProFacingWalletProduct): ProFacingWalletProduct {
  const { activation_id, activation_config, activation_status, is_active, ...rest } = raw
  const activation: WalletProductOrg | undefined =
    activation_id != null
      ? { id: activation_id, activation_config: activation_config ?? {}, status: activation_status ?? null }
      : undefined
  return {
    ...rest,
    activation,
    activation_status: activation_status ?? null,
    is_active: is_active ?? false,
  }
}

export function resolveIntegrationFromCategories(
  categories: CategorizedIntegrations | null,
  integrationId: number,
  fallback: ProFacingWalletProduct
): ProFacingWalletProduct {
  if (!categories) return fallback
  for (const items of Object.values(categories)) {
    const match = items.find((item) => item.id === integrationId)
    if (match) return match
  }
  return fallback
}

export function categorizeIntegrations(integrations: ProFacingWalletProduct[]): CategorizedIntegrations {
  const categorizedIntegrations: CategorizedIntegrations = {}
  integrations.forEach((integration) => {
    const category = integration.category || 'uncategorized'
    if (!categorizedIntegrations[category]) {
      categorizedIntegrations[category] = []
    }
    categorizedIntegrations[category].push(integration)
  })
  return categorizedIntegrations
}

export const doFetchWalletProductIntegrations = (orgId: number): Promise<CategorizedIntegrations> => {
  const restClientInstance = restClient(window.API_ROOT + '/api')
  return restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/wallet_products/?paginate=false`,
  }).then((response: { data: RawProFacingWalletProduct[] }) =>
    categorizeIntegrations(response.data.map(normalizeIntegration))
  )
}

export const WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST = 'WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST'
export const WALLET_PRODUCT_INTEGRATIONS_FETCH_SUCCESS = 'WALLET_PRODUCT_INTEGRATIONS_FETCH_SUCCESS'
export const WALLET_PRODUCT_INTEGRATIONS_FETCH_FAILURE = 'WALLET_PRODUCT_INTEGRATIONS_FETCH_FAILURE'
export const WALLET_PRODUCT_INTEGRATION_UPDATE = 'WALLET_PRODUCT_INTEGRATION_UPDATE'

export interface WalletProductIntegrationsState {
  orgId: number | null
  categories: CategorizedIntegrations | null
  isLoading: boolean
  error: string | null
}

export interface FetchRequestAction {
  type: typeof WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST
  payload: { orgId: number }
}

export interface FetchSuccessAction {
  type: typeof WALLET_PRODUCT_INTEGRATIONS_FETCH_SUCCESS
  payload: { orgId: number; categories: CategorizedIntegrations }
}

export interface FetchFailureAction {
  type: typeof WALLET_PRODUCT_INTEGRATIONS_FETCH_FAILURE
  payload: { orgId: number; error: string }
}

export interface UpdateIntegrationAction {
  type: typeof WALLET_PRODUCT_INTEGRATION_UPDATE
  payload: { integration: ProFacingWalletProduct }
}

export type WalletProductIntegrationsAction =
  | FetchRequestAction
  | FetchSuccessAction
  | FetchFailureAction
  | UpdateIntegrationAction

export const walletProductIntegrationsActions = {
  fetchIntegrationsRequest: (orgId: number): FetchRequestAction => ({
    type: WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST,
    payload: { orgId },
  }),
  fetchIntegrationsSuccess: (orgId: number, categories: CategorizedIntegrations): FetchSuccessAction => ({
    type: WALLET_PRODUCT_INTEGRATIONS_FETCH_SUCCESS,
    payload: { orgId, categories },
  }),
  fetchIntegrationsFailure: (orgId: number, error: string): FetchFailureAction => ({
    type: WALLET_PRODUCT_INTEGRATIONS_FETCH_FAILURE,
    payload: { orgId, error },
  }),
  updateIntegration: (integration: ProFacingWalletProduct): UpdateIntegrationAction => ({
    type: WALLET_PRODUCT_INTEGRATION_UPDATE,
    payload: { integration },
  }),
}

// Named exports for backward compatibility
export const fetchIntegrationsRequest = walletProductIntegrationsActions.fetchIntegrationsRequest
export const fetchIntegrationsSuccess = walletProductIntegrationsActions.fetchIntegrationsSuccess
export const fetchIntegrationsFailure = walletProductIntegrationsActions.fetchIntegrationsFailure
export const updateIntegration = walletProductIntegrationsActions.updateIntegration

const initialState: WalletProductIntegrationsState = {
  orgId: null,
  categories: null,
  isLoading: false,
  error: null,
}

const walletProductIntegrationsReducer: Reducer<WalletProductIntegrationsState, WalletProductIntegrationsAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST:
      return {
        ...state,
        orgId: action.payload.orgId,
        categories: state.orgId === action.payload.orgId ? state.categories : null,
        isLoading: true,
        error: null,
      }
    case WALLET_PRODUCT_INTEGRATIONS_FETCH_SUCCESS:
      if (state.orgId !== action.payload.orgId) {
        return state
      }
      return {
        ...state,
        categories: action.payload.categories,
        isLoading: false,
        error: null,
      }
    case WALLET_PRODUCT_INTEGRATIONS_FETCH_FAILURE:
      if (state.orgId !== action.payload.orgId) {
        return state
      }
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      }
    case WALLET_PRODUCT_INTEGRATION_UPDATE: {
      const { integration } = action.payload
      if (!state.categories) return state

      const newCategories: CategorizedIntegrations = {}
      for (const category in state.categories) {
        newCategories[category] = state.categories[category].map((item) =>
          item.id === integration.id ? integration : item
        )
      }
      return {
        ...state,
        categories: newCategories,
      }
    }
    default:
      return state
  }
}

const selectWalletProductIntegrations = (state: RootState): WalletProductIntegrationsState | undefined =>
  state.walletProductIntegrations

export const walletProductIntegrationsSelectors = {
  getCategories: createSelector([selectWalletProductIntegrations], (integrations) => integrations?.categories ?? null),
  getIsLoading: createSelector([selectWalletProductIntegrations], (integrations) => integrations?.isLoading ?? false),
  getError: createSelector([selectWalletProductIntegrations], (integrations) => integrations?.error ?? null),
  getOrgId: createSelector([selectWalletProductIntegrations], (integrations) => integrations?.orgId ?? null),
}

export const findWalletProductIntegrationById = (
  categories: CategorizedIntegrations | null,
  walletProductId: number
): ProFacingWalletProduct | undefined => {
  if (!categories) return undefined
  for (const integrations of Object.values(categories)) {
    const match = integrations.find((integration) => integration.id === walletProductId)
    if (match) return match
  }
  return undefined
}

export function* fetchWalletProductIntegrationsSaga(
  action: FetchRequestAction
): Generator<unknown, void, CategorizedIntegrations> {
  const { orgId } = action.payload
  try {
    const categories = yield call(doFetchWalletProductIntegrations, orgId)
    yield put(walletProductIntegrationsActions.fetchIntegrationsSuccess(orgId, categories))
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to fetch integrations'
    yield put(walletProductIntegrationsActions.fetchIntegrationsFailure(orgId, message))
  }
}

export function* walletProductIntegrationsSagas(): Generator {
  yield all([takeEvery(WALLET_PRODUCT_INTEGRATIONS_FETCH_REQUEST, fetchWalletProductIntegrationsSaga)])
}

export default walletProductIntegrationsReducer
